import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Trash2, Play, ListChecks, Repeat } from "lucide-react";

export function SaveTemplateDialog({ task, onClose }) {
  const { t } = useApp();
  const [name, setName] = useState(task.title);
  const [dueDays, setDueDays] = useState("");
  const save = async () => {
    try {
      await api.post(`/task-templates/from-task/${task.id}`, { name, due_days: dueDays === "" ? null : Number(dueDays) });
      toast.success(t("tpl.saved"));
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="save-template-dialog">
        <DialogHeader><DialogTitle>{t("tpl.saveAs")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("tpl.name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name-input" /></div>
          <div><Label>{t("tpl.dueDays")}</Label><Input type="number" min="0" value={dueDays} onChange={(e) => setDueDays(e.target.value)} placeholder="—" data-testid="template-duedays-input" /></div>
          <p className="text-xs text-muted-foreground">{t("tpl.saveHint")} ({(task.checklist || []).length} {t("task.checklist").toLowerCase()})</p>
          <Button className="w-full" onClick={save} disabled={!name.trim()} data-testid="template-save-button">{t("common.save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TaskTemplatesDialog({ boardId, entityId, onClose, onApplied }) {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const load = () => api.get("/task-templates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = async (tpl) => {
    try {
      await api.post(`/task-templates/${tpl.id}/apply`, { board_id: boardId || null, entity_id: entityId || null });
      toast.success(t("tpl.applied"));
      onApplied();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
  };
  const remove = async (id) => {
    try { await api.delete(`/task-templates/${id}`); toast.success(t("msg.deleted")); load(); }
    catch { toast.error(t("msg.error")); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="task-templates-dialog">
        <DialogHeader><DialogTitle>{t("tpl.title")}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {items.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="templates-empty">{t("tpl.empty")}</div>}
          {items.map((tpl) => (
            <div key={tpl.id} className="flex items-center gap-3 rounded-md border border-border p-3" data-testid={`template-row-${tpl.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{tpl.name}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" />{(tpl.checklist || []).length}</span>
                  {tpl.recurrence && <span className="flex items-center gap-1"><Repeat className="h-3 w-3" />{t(`task.rec${tpl.recurrence === "weekly" ? "Weekly" : "Monthly"}`)}</span>}
                  {tpl.due_days != null && <span>+{tpl.due_days} {t("tpl.days")}</span>}
                  <Badge variant="outline" className="text-[10px]">{t(`task.${tpl.priority || "medium"}`)}</Badge>
                </div>
              </div>
              <Button size="sm" onClick={() => apply(tpl)} data-testid={`template-apply-${tpl.id}`}><Play className="h-3.5 w-3.5 mr-1" />{t("tpl.use")}</Button>
              <Button size="icon" variant="ghost" onClick={() => remove(tpl.id)} data-testid={`template-delete-${tpl.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
